import UserPrivateComponent from "@/components/PrivacyComponent/UserPrivateComponent";
import { useSyncGoogleSheetMutation } from "@/redux/rtk/features/leads/leadsApi";
import { Button, Form, Input, Alert, Tag, Divider } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SiGooglesheets } from "react-icons/si";
import { AiOutlineCloudSync, AiOutlineCheckCircle, AiOutlineCloseCircle, AiOutlineInfoCircle } from "react-icons/ai";
import toast from "react-hot-toast";

export default function SyncGoogleSheetModal({ onClose }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [syncGoogleSheet, { isLoading }] = useSyncGoogleSheetMutation();
  const [syncResult, setSyncResult] = useState(null);

  const onFinish = async (values) => {
    setSyncResult(null);
    try {
      const sId = values.spreadsheetId ? values.spreadsheetId.trim() : undefined;
      const sName = values.sheetName ? values.sheetName.trim() : undefined;

      // Cache in localStorage for future syncs
      if (sId) {
        localStorage.setItem("last_google_spreadsheet_id", sId);
      }
      if (sName) {
        localStorage.setItem("last_google_sheet_name", sName);
      }

      const payload = {
        spreadsheetId: sId,
        sheetName: sName,
        apiKey: values.apiKey ? values.apiKey.trim() : undefined,
      };

      const resp = await syncGoogleSheet(payload).unwrap();
      if (resp && resp.data) {
        setSyncResult(resp.data);
        toast.success(resp.message || t("lead.syncSuccess"));
      }
    } catch (err) {
      const errorMessage =
        err?.data?.error ||
        err?.data?.message ||
        err?.error ||
        t("errors.somethingWentWrong") ||
        "Error syncing with Google Sheet";
      toast.error(errorMessage);
    }
  };

  return (
    <UserPrivateComponent permission="create-lead">
      <div className="flex flex-col gap-4">
        {/* Header Banner */}
        <div className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg">
          <div className="text-3xl text-emerald-600 dark:text-emerald-400">
            <SiGooglesheets />
          </div>
          <div>
            <h4 className="font-bold text-gray-800 dark:text-gray-200 text-sm">
              {t("lead.syncGoogleSheetTitle")}
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("lead.syncGoogleSheetDesc")}
            </p>
          </div>
        </div>

        {/* Sync Form */}
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            spreadsheetId: localStorage.getItem("last_google_spreadsheet_id") || "",
            sheetName: localStorage.getItem("last_google_sheet_name") || "Sheet1",
          }}
          className="flex flex-col gap-3"
        >
          <Form.Item
            label={t("lead.spreadsheetId")}
            name="spreadsheetId"
            tooltip={t("lead.spreadsheetIdTooltip")}
          >
            <Input
              allowClear
            />
          </Form.Item>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Form.Item
              label={t("lead.sheetTabName")}
              name="sheetName"
              tooltip={t("lead.sheetTabNameTooltip")}
            >
              <Input placeholder="Sheet1" />
            </Form.Item>
          </div>

          <div className="flex justify-end items-center gap-2 mt-2">
            <Button onClick={onClose} disabled={isLoading}>
              {t("common.cancel") || "إلغاء"}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              className="bg-emerald-600 hover:bg-emerald-500 flex items-center gap-1.5"
            >
              <AiOutlineCloudSync className="text-lg" />
              <span>{isLoading ? t("lead.syncing") : t("lead.startSyncNow")}</span>
            </Button>
          </div>
        </Form>

        {/* Results Summary Section */}
        {syncResult && (
          <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-800 border rounded-lg flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="font-bold text-sm text-gray-700 dark:text-gray-300">
                {t("lead.syncResults")}
              </span>
              <Tag color="blue">{syncResult.totalRows || 0} {t("lead.totalRows")}</Tag>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded border border-green-200 dark:border-green-800">
                <div className="flex items-center justify-center gap-1 text-xs">
                  <AiOutlineCheckCircle /> {t("lead.successAndAdded")}
                </div>
                <div className="text-lg font-extrabold">{syncResult.successCount}</div>
              </div>

              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded border border-amber-200 dark:border-amber-800">
                <div className="flex items-center justify-center gap-1 text-xs">
                  <AiOutlineInfoCircle /> {t("lead.duplicateSkipped")}
                </div>
                <div className="text-lg font-extrabold">{syncResult.duplicatesCount}</div>
              </div>

              <div className="p-2 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded border border-red-200 dark:border-red-800">
                <div className="flex items-center justify-center gap-1 text-xs">
                  <AiOutlineCloseCircle /> {t("lead.errorsAndSkipped")}
                </div>
                <div className="text-lg font-extrabold">{syncResult.errorsCount}</div>
              </div>
            </div>

            {syncResult.errors && syncResult.errors.length > 0 && (
              <div className="mt-2">
                <span className="text-xs font-semibold text-gray-500 block mb-1">
                  {t("lead.skippedOrFailedDetails")}
                </span>
                <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-900 text-xs flex flex-col gap-1">
                  {syncResult.errors.map((errItem, idx) => (
                    <div key={idx} className="flex justify-between items-center border-b border-gray-100 dark:border-gray-800 pb-1 last:border-none">
                      <span className="font-semibold text-gray-700 dark:text-gray-300">
                        {errItem.row ? `الصف ${errItem.row}: ` : ""}{errItem.name || "سجل"}
                      </span>
                      <span className="text-amber-600 dark:text-amber-400 text-[11px]">
                        {errItem.error}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </UserPrivateComponent>
  );
}
